import { createAction, type PayloadAction, type Reducer } from '@reduxjs/toolkit';

export const undo = createAction('history/undo');
export const redo = createAction('history/redo');

interface HistoryState<T> {
  past: T[];
  present: T;
  future: T[];
}

interface UndoableOptions {
    filter?: (action: PayloadAction<any>) => boolean;
}

export default function undoable<T>(
  reducer: Reducer<T>,
  options: UndoableOptions = {}
): Reducer<HistoryState<T>> {
  const { filter } = options;
  const initialState: HistoryState<T> = {
    past: [],
    present: reducer(undefined, { type: '@@INIT' }),
    future: [],
  };

  return (state = initialState, action) => {
    const { past, present, future } = state;

    if (undo.match(action)) {
      if (past.length === 0) {
        return state;
      }
      const previous = past[past.length - 1];
      const newPast = past.slice(0, past.length - 1);
      return {
        past: newPast,
        present: previous,
        future: [present, ...future],
      };
    }

    if (redo.match(action)) {
      if (future.length === 0) {
        return state;
      }
      const next = future[0];
      const newFuture = future.slice(1);
      return {
        past: [...past, present],
        present: next,
        future: newFuture,
      };
    }

    const newPresent = reducer(present, action);

    if (present === newPresent) {
      return state;
    }

    if (filter && !filter(action as any)) {
        return {
            ...state,
            present: newPresent
        }
    }

    return {
      past: [...past, present],
      present: newPresent,
      future: [],
    };
  };
}
